import { useState } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, X, Minus } from "lucide-react";
import { formatMoney } from "@/lib/money";
import { agencyApi, apiErr } from "./agencyApi";
import { computeGroupDescriptions } from "./lineDescriptions";
import "./agency.css";

const PRODUCTION_TYPES = ["Corporate Interview", "Corporate Film", "Product Film", "Event Coverage", "TVC"];

function Steps({ step, tvc, fallback }) {
  const step2Label = tvc ? "Request a Quote" : fallback ? "Request a Quote" : "Production Cost Estimate";
  const items = [{ n: "01", label: "Project Basics" }, { n: "02", label: step2Label }];
  return (
    <div className="ag-est-steps" data-testid="agency-estimator-steps">
      {items.map((it, i) => (
        <span key={it.n} className={i + 1 <= step ? "on" : ""}>{it.n} {it.label}</span>
      ))}
    </div>
  );
}

function Tips({ title, items }) {
  return (
    <div className="ag-card" style={{ background: "var(--paper-2)", border: "none" }}>
      <p className="ag-eyebrow" style={{ marginBottom: 14 }}>{title}</p>
      {items.map((t, i) => (
        <div key={i} className="ag-est-tip">
          <div className="ag-h3" style={{ fontSize: 14 }}>{t.h}</div>
          <p className="ag-small" style={{ marginTop: 4 }}>{t.b}</p>
        </div>
      ))}
    </div>
  );
}

function BasicsStep({ form, setForm, onContinue, geoLabel, building, cameraSelectableTypes, cameraSetups }) {
  const ready = form.production_type && form.shoot_location.trim() && Number(form.shoot_days) > 0;
  const showCameraSelector = (cameraSelectableTypes || []).includes(form.production_type);
  return (
    <div className="ag-grid" style={{ gridTemplateColumns: "1fr 300px", gap: 40, alignItems: "start" }}>
      <div>
        <h1 className="ag-h1" data-testid="agency-estimator-title">What are you looking to produce?</h1>
        <p className="ag-body" style={{ marginTop: 10, marginBottom: 30 }}>Four quick fields. Two of them are optional.</p>

        <div className="ag-field">
          <span className="ag-label">Production Type</span>
          <div className="ag-est-chips" style={{ marginTop: 10 }}>
            {PRODUCTION_TYPES.map((pt) => (
              <button key={pt} type="button" data-testid={`agency-estimator-chip-${pt.toLowerCase().replace(/\s+/g, "-")}`}
                className={`ag-est-chip${form.production_type === pt ? " active" : ""}`}
                onClick={() => setForm((f) => ({ ...f, production_type: pt }))}>{pt}</button>
            ))}
          </div>
        </div>

        {showCameraSelector && (
          <div className="ag-field" data-testid="agency-estimator-camera-field">
            <span className="ag-label">Camera Package</span>
            <div className="ag-est-chips" style={{ marginTop: 10 }}>
              {(cameraSetups || []).map((cs) => (
                <button key={cs.key} type="button" data-testid={`agency-estimator-camera-chip-${cs.key.toLowerCase()}`}
                  className={`ag-est-chip${form.camera_setup === cs.key ? " active" : ""}`}
                  onClick={() => setForm((f) => ({ ...f, camera_setup: cs.key }))}>
                  {cs.label}
                  <span className="ag-small" style={{ display: "block", opacity: .65, marginTop: 2, textTransform: "none" }}>{cs.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="ag-grid ag-grid-2" style={{ gap: 18 }}>
          <div className="ag-field">
            <span className="ag-label">Shoot Location</span>
            <input className="ag-input" style={{ marginTop: 8 }} placeholder="e.g. Bengaluru" value={form.shoot_location}
              onChange={(e) => setForm((f) => ({ ...f, shoot_location: e.target.value }))} data-testid="agency-estimator-location" />
          </div>
          <div className="ag-field">
            <span className="ag-label">Shoot Days</span>
            <input className="ag-input" style={{ marginTop: 8 }} type="number" min={1} value={form.shoot_days}
              onChange={(e) => setForm((f) => ({ ...f, shoot_days: e.target.value }))} data-testid="agency-estimator-days" />
          </div>
        </div>
        <div className="ag-field">
          <span className="ag-label">Working Title <span style={{ textTransform: "none", fontWeight: 400 }}>· optional</span></span>
          <input className="ag-input" style={{ marginTop: 8 }} placeholder="e.g. Leadership interviews — Q4" value={form.working_title}
            onChange={(e) => setForm((f) => ({ ...f, working_title: e.target.value }))} data-testid="agency-estimator-title-input" />
        </div>
        <div className="ag-field">
          <span className="ag-label">Short Brief <span style={{ textTransform: "none", fontWeight: 400 }}>· optional</span></span>
          <textarea className="ag-input" rows={3} style={{ marginTop: 8, resize: "vertical" }} placeholder="Anything that affects scope — locations, talent, deliverables."
            value={form.brief} onChange={(e) => setForm((f) => ({ ...f, brief: e.target.value }))} data-testid="agency-estimator-brief" />
        </div>
        {/* Regression v6 fix pass (2026-10), Part 4 item 1 — building the estimate can take
            several seconds (the brief goes through the requirements interpreter server-side,
            up to a 12s timeout). This button used to stay fully enabled with zero feedback, so
            the page looked frozen and a second click fired a second prefill. */}
        <button className="ag-btn ag-btn-gold" disabled={!ready || building} onClick={onContinue} data-testid="agency-estimator-continue">
          {building ? "Building your estimate…" : "Continue to Estimate"}
        </button>
        {building && (
          <div className="ag-stack" style={{ marginTop: 18 }} data-testid="agency-estimator-building">
            <p className="ag-small">
              {(form.brief || "").trim()
                ? "Reading your brief and matching it to our production items — this can take a few seconds."
                : "Pulling the starting point for this production type…"}
            </p>
            {[0, 1, 2].map((i) => <div key={i} className="ag-skeleton" style={{ height: 38, marginTop: 10, borderRadius: 6 }} />)}
          </div>
        )}
      </div>
      <Tips title="How this works" items={[
        { h: "We pre-fill a starting point", b: "Your production type matches a package. Step 2 opens with its production items — all editable." },
        { h: "Production costs only", b: "Editing, scripting and storyboarding are quoted separately." },
        { h: "Nothing is booked", b: "NJM reviews the estimate and returns a formal quote." },
      ]} />
    </div>
  );
}

const CURRENCY_SYMBOL = { USD: "$", INR: "\u20b9" };
// E4 shoot-day scaling (2026-11) — frontend-side instant recompute only, mirroring
// modules/build.py::_apply_shoot_day_scaling exactly (qty stays the per-day count; amount =
// rate x qty x days_multiplier). The server re-verifies and re-prices everything from scratch
// at submit regardless — this is purely for a live total while editing, same trust level as
// every other client-side qty edit in this file already had before E4.
function priceLine(rate, qty, daysMultiplier, currency) {
  if (rate == null) return { amount: null, amount_breakdown: null };
  const amount = Math.round(rate * qty * daysMultiplier * 100) / 100;
  if (daysMultiplier > 1) {
    const symbol = CURRENCY_SYMBOL[currency] || "";
    const qtyPart = qty !== 1 ? `${qty} \u00d7 ` : "";
    return { amount, amount_breakdown: `${symbol}${rate} \u00d7 ${qtyPart}${daysMultiplier} days` };
  }
  return { amount, amount_breakdown: null };
}

function LineRow({ line, currency, onQtyChange, onRemove, effectiveDescription }) {
  return (
    <div className="ag-cs-row ag-cs-line" style={{ gridTemplateColumns: "1fr 90px 150px 40px" }} data-testid={`agency-estimator-line-${line.line_id}`}>
      <div className="ag-cs-desc">{line.line_label || line.display_name}
        {line.tag === "REQUESTED" ? <span className="ag-cs-chip ag-cs-chip-added" style={{ marginLeft: 8 }} data-testid={`agency-estimator-line-tag-${line.line_id}`}>Requested</span>
          : <span className="ag-cs-chip ag-cs-chip-current" style={{ marginLeft: 8 }} data-testid={`agency-estimator-line-tag-${line.line_id}`}>Suggested</span>}
        {effectiveDescription && <div className="ag-small" style={{ marginTop: 3, opacity: .65 }} data-testid={`agency-estimator-line-desc-${line.line_id}`}>{effectiveDescription}</div>}
      </div>
      <div className="ag-est-qty-cell">
        {line.editable_qty ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button type="button" className="ag-est-qty-btn" onClick={() => onQtyChange(Math.max(1, (line.qty || 1) - 1))} data-testid={`agency-estimator-qty-minus-${line.line_id}`}><Minus size={11} /></button>
            <span className="ag-cs-num" style={{ minWidth: 18, textAlign: "center" }}>{line.qty}</span>
            <button type="button" className="ag-est-qty-btn" onClick={() => onQtyChange((line.qty || 1) + 1)} data-testid={`agency-estimator-qty-plus-${line.line_id}`}><Plus size={11} /></button>
            {line.unit && <span className="ag-small" style={{ opacity: .55, marginLeft: 2 }}>/ {line.unit}</span>}
          </div>
        ) : <span className="ag-small">1 day</span>}
      </div>
      <div className="ag-cs-num ag-cs-amt">
        {line.rate_missing ? "NJM to quote" : formatMoney(line.amount, currency)}
        {!line.rate_missing && line.amount_breakdown && <div className="ag-small" style={{ opacity: .6, fontWeight: 400 }} data-testid={`agency-estimator-line-breakdown-${line.line_id}`}>{line.amount_breakdown}</div>}
      </div>
      <button type="button" className="ag-cs-cbtn" onClick={onRemove} aria-label="Remove" data-testid={`agency-estimator-remove-${line.line_id}`}><X size={14} /></button>
    </div>
  );
}

// Wave 2 (2026-09, readable lines) — groups already-flat, still individually-editable `lines`
// by `section_heading` (Presentation Group) purely for DISPLAY: one heading + one Client
// Description per group, so a group with several distinct services (e.g. "Cinematography"
// holding a camera kit, a DOP shift and DIT) never repeats the same bare group label 3x at 3
// different prices. Recomputed on every render from whatever's currently in `lines`/
// `addedLines`, so qty edits/add/remove never fall out of sync with the grouping.
// Line sub-text fix (2026-11): the group's own description is now the text 2+ of its lines
// actually share (see lineDescriptions.js), not just whichever line happened to be first.
function groupLinesBySection(allLines) {
  const order = [];
  const groups = {};
  const groupDesc = computeGroupDescriptions(allLines, (l) => l.section_heading || l.display_name || "Additional services", (l) => l.description);
  for (const l of allLines) {
    const heading = l.section_heading || l.display_name || "Additional services";
    if (!groups[heading]) { groups[heading] = { heading, description: groupDesc[heading] || "", lines: [] }; order.push(heading); }
    groups[heading].lines.push(l);
  }
  return order.map((h) => groups[h]);
}

function AddRequirementPanel({ onAdd, onClose, catalogFn, currency }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState({ description: "", qty: 1, notes: "" });

  useEffect(() => {
    const t = setTimeout(() => { catalogFn(q).then((r) => setItems(r.data.items || [])).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ag-card" style={{ marginTop: 14, background: "var(--paper-2)" }} data-testid="agency-estimator-add-requirement-panel">
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <Search size={14} className="ag-small" />
        <input className="ag-input" placeholder="Search production items…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="agency-estimator-catalog-search" autoFocus />
        <button className="ag-link" onClick={onClose} data-testid="agency-estimator-close-add-requirement">Close</button>
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto" }}>
        {items.map((it) => (
          <div key={it.canonical_id} className="ag-cs-row" style={{ gridTemplateColumns: "1fr 140px 70px", padding: "10px 6px" }} data-testid={`agency-estimator-catalog-item-${it.canonical_id}`}>
            <span className="ag-body">{it.display_name}{it.group_label && <span className="ag-small" style={{ display: "block", opacity: 0.6, marginTop: 2 }}>{it.group_label}</span>}</span>
            <span className="ag-small" style={{ textAlign: "right" }}>{it.rate_missing ? "NJM to quote" : `${formatMoney(it.client_rate, currency)} / ${it.unit || "unit"}`}</span>
            <button className="ag-btn ag-btn-ghost" style={{ padding: "6px 12px" }} onClick={() => onAdd({ canonical_id: it.canonical_id, display_name: it.display_name, qty: 1, client_rate: it.client_rate, unit: it.unit })} data-testid={`agency-estimator-add-catalog-${it.canonical_id}`}>Add</button>
          </div>
        ))}
        {items.length === 0 && <p className="ag-small" style={{ padding: "8px 6px" }}>No matches. Not on our rate card? Add it as an other requirement below.</p>}
      </div>
      <div style={{ marginTop: 14, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
        {/* Regression v6 fix pass (2026-10), Part 4 item 2 — "other requirement" is the agency-
            facing name for an off-rate-card line, and its price reads "NJM to quote" — the SAME
            words the submitted request and the staff queue already use for an unpriced line
            (QuoteRequestDetail.jsx, modules/requirements_interpreter.py's placeholder label).
            It used to say "price to be confirmed by NJM"/"Price TBC by NJM" here only. */}
        {!customOpen ? (
          <button className="ag-link" onClick={() => setCustomOpen(true)} data-testid="agency-estimator-open-custom">+ Other requirement (NJM to quote)</button>
        ) : (
          <div className="ag-stack">
            <input className="ag-input" placeholder="Description" value={custom.description} onChange={(e) => setCustom((c) => ({ ...c, description: e.target.value }))} data-testid="agency-estimator-custom-description" />
            <div className="ag-grid ag-grid-2" style={{ gap: 12 }}>
              <input className="ag-input" type="number" min={1} placeholder="Qty" value={custom.qty} onChange={(e) => setCustom((c) => ({ ...c, qty: e.target.value }))} data-testid="agency-estimator-custom-qty" />
              <input className="ag-input" placeholder="Notes (optional)" value={custom.notes} onChange={(e) => setCustom((c) => ({ ...c, notes: e.target.value }))} data-testid="agency-estimator-custom-notes" />
            </div>
            <button className="ag-btn ag-btn-dark" disabled={!custom.description.trim()}
              onClick={() => { onAdd({ custom: true, ...custom, qty: Number(custom.qty) || 1 }); setCustom({ description: "", qty: 1, notes: "" }); setCustomOpen(false); }}
              data-testid="agency-estimator-add-custom">Add other requirement</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ReferenceVideoPicker({ selectedIds, onToggle, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    setLoading(true);
    agencyApi.archives(category || undefined).then((r) => {
      setItems(r.data.items || []);
      setCategories(r.data.categories || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="ag-card" style={{ marginTop: 12, background: "var(--paper-2)" }} data-testid="agency-estimator-reference-picker">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <p className="ag-small">Pick up to 5. {selectedIds.length}/5 selected.</p>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {categories.length > 0 && (
            <select className="ag-input" style={{ padding: "4px 8px", fontSize: 12 }} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="agency-estimator-reference-category">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button className="ag-link" onClick={onClose} data-testid="agency-estimator-close-reference-picker">Close</button>
        </div>
      </div>
      {loading ? <p className="ag-small">Loading…</p> : items.length === 0 ? (
        <p className="ag-small">No archived work samples are available to attach yet.</p>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          {items.map((it) => {
            const selected = selectedIds.includes(it.id);
            const disabled = !selected && selectedIds.length >= 5;
            return (
              <div key={it.id} style={{ width: 110 }} data-testid={`agency-estimator-reference-item-${it.id}`}>
                <div style={{ position: "relative", width: 110, height: 66, background: "#ececec", borderRadius: 6, overflow: "hidden" }}>
                  {it.thumbnail_url ? <img src={it.thumbnail_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} loading="lazy" decoding="async" /> : null}
                </div>
                <div className="ag-small" style={{ marginTop: 4, fontSize: 10.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={it.title}>{it.title}</div>
                <button type="button" className={`ag-btn ${selected ? "ag-btn-dark" : "ag-btn-ghost"}`} style={{ padding: "4px 8px", fontSize: 11, marginTop: 4, width: "100%" }}
                  disabled={disabled} onClick={() => onToggle(it.id)} data-testid={`agency-estimator-reference-toggle-${it.id}`}>
                  {selected ? "Selected ✓" : "Select"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// PM Feedback Batch 1, item 2 (2026-09) — `lines`/`addedLines`/`customReqs` are now OWNED by
// the parent (`estimateData`/`setEstimateData`), not local state here. Previously this
// component unmounted every time "Edit basics" was used (phase -> "basics"), and its own
// `useEffect(() => setLines(...), [prefill])` re-ran on remount, silently discarding every
// qty/add/remove/custom-requirement edit the agency had made. Being fully controlled means
// going back to basics and forward again reuses the SAME state — nothing is lost.
function EstimateStep({ form, prefill, data, setData, currency, gstApplicable, onEditBasics, onSubmit, submitting }) {
  const [addOpen, setAddOpen] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const { lines, addedLines, customReqs, referenceVideoIds = [] } = data;

  const removeLine = (lineId) => setData((d) => ({ ...d, lines: d.lines.filter((l) => l.line_id !== lineId) }));
  const setQty = (lineId, qty) => setData((d) => ({ ...d, lines: d.lines.map((l) => (l.line_id === lineId ? { ...l, qty, ...priceLine(l.client_rate, qty, l.days_multiplier || 1, currency) } : l)) }));
  const setAddedQty = (lineId, qty) => setData((d) => ({ ...d, addedLines: d.addedLines.map((l) => (l.line_id === lineId ? { ...l, qty, ...priceLine(l.client_rate, qty, l.days_multiplier || 1, currency) } : l)) }));
  const clearAll = () => setData((d) => ({ ...d, lines: [], addedLines: [], customReqs: [] }));
  const toggleReference = (id) => setData((d) => {
    const cur = d.referenceVideoIds || [];
    if (cur.includes(id)) return { ...d, referenceVideoIds: cur.filter((x) => x !== id) };
    if (cur.length >= 5) return d;
    return { ...d, referenceVideoIds: [...cur, id] };
  });

  const addCatalog = (item) => {
    if (item.custom) { setData((d) => ({ ...d, customReqs: [...d.customReqs, item] })); setAddOpen(false); return; }
    const shootDays = Number(form.shoot_days) || 1;
    const scales = ["per day", "per shift"].includes((item.unit || "").trim().toLowerCase());
    const daysMultiplier = scales ? shootDays : 1;
    setData((d) => ({ ...d, addedLines: [...d.addedLines, { ...item, line_id: `added-${item.canonical_id}`, editable_qty: true, tag: "REQUESTED", line_label: item.display_name, section_heading: item.group_label || item.display_name, days_multiplier: daysMultiplier, ...priceLine(item.client_rate, item.qty, daysMultiplier, currency) }] }));
    setAddOpen(false);
  };
  const removeAdded = (lineId) => setData((d) => ({ ...d, addedLines: d.addedLines.filter((l) => l.line_id !== lineId) }));
  const removeCustom = (idx) => setData((d) => ({ ...d, customReqs: d.customReqs.filter((_, i) => i !== idx) }));

  const subtotal = [...lines, ...addedLines].reduce((s, l) => s + (l.amount || 0), 0);
  const gst = gstApplicable ? Math.round(subtotal * 0.18 * 100) / 100 : 0;
  const grandTotal = subtotal + gst;
  const sections = groupLinesBySection([...lines, ...addedLines]);
  const ledger = prefill.requirements_ledger || [];

  const submit = () => onSubmit({
    // TTL/resilience fix (2026-09-27): canonical_id/tag/requirement_text ride along so submit
    // can still re-price this exact line directly (never trusting its rate/amount) even if
    // the server-side prefill cache has since expired — see agency_estimator_submit.
    included_lines: lines.map((l) => ({ line_id: l.line_id, qty: l.qty, canonical_id: l.canonical_id, tag: l.tag, requirement_text: l.requirement_text })),
    added_catalog_lines: addedLines.map((l) => ({ canonical_id: l.canonical_id, qty: l.qty })),
    custom_requirements: customReqs.map((c) => ({ description: c.description, qty: c.qty, notes: c.notes })),
    reference_video_ids: referenceVideoIds,
    grandTotalPreview: grandTotal,
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
        <h1 className="ag-h1" style={{ fontSize: 30 }} data-testid="agency-estimator-package-name">{form.production_type}</h1>
        <button className="ag-link" onClick={onEditBasics} data-testid="agency-estimator-edit-basics">Edit basics</button>
      </div>
      <p className="ag-small" style={{ marginTop: 4 }}>{form.production_type} / {form.shoot_location} / {form.shoot_days} shoot day{Number(form.shoot_days) > 1 ? "s" : ""}</p>
      <p className="ag-body" style={{ marginTop: 16, marginBottom: 18 }}>We've pre-filled a starting point for your production type. Edit freely — change quantities, remove anything, add your own.</p>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <button className="ag-link" onClick={clearAll} data-testid="agency-estimator-clear-all">Clear all</button>
      </div>

      {(form.brief || "").trim() && (
        <div className="ag-card" style={{ marginBottom: 18, background: "var(--paper-2)" }} data-testid="agency-estimator-brief-echo">
          <p className="ag-eyebrow" style={{ marginBottom: 6 }}>Your brief</p>
          <p className="ag-small" style={{ whiteSpace: "pre-wrap" }}>{form.brief}</p>
        </div>
      )}

      {ledger.length > 0 && (
        <div className="ag-card" style={{ marginBottom: 18, background: "var(--paper-2)" }} data-testid="agency-estimator-requirements-ledger">
          <p className="ag-eyebrow" style={{ marginBottom: 10 }}>What we understood from your brief</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {ledger.map((e, i) => (
              <div key={i} className="ag-small" data-testid={`agency-estimator-ledger-item-${i}`}>
                <span style={{ fontWeight: 600, color: "var(--ink)" }}>You requested: {e.requirement_text}</span>
                {e.line_label ? <span> → {e.line_label}{e.status === "ASSUMED" ? " (assumed)" : ""}</span> : <span style={{ color: "var(--amber)" }}> → couldn't match this automatically, added as TBC</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="ag-est-lines">
      <div className="ag-cs-row ag-cs-header-row" style={{ position: "static", gridTemplateColumns: "1fr 90px 150px 40px" }}>
        <div>Item</div><div>Qty</div><div>Rate</div><div />
      </div>
      {sections.map((sec) => (
        <div key={sec.heading} data-testid={`agency-estimator-section-${sec.heading.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="ag-cs-group-title" style={{ paddingLeft: 4 }}>{sec.heading} <span className="ag-small">· {sec.lines.length} item{sec.lines.length > 1 ? "s" : ""}</span></div>
          {sec.description && <p className="ag-small" style={{ padding: "0 4px 8px", opacity: .7 }} data-testid={`agency-estimator-section-desc-${sec.heading.toLowerCase().replace(/\s+/g, "-")}`}>{sec.description}</p>}
          {sec.lines.map((l) => (
            <LineRow key={l.line_id} line={l} currency={currency}
              effectiveDescription={l.description && l.description.trim() !== (sec.description || "").trim() ? l.description : null}
              onQtyChange={(q) => (l.line_id.startsWith("added-") ? setAddedQty(l.line_id, q) : setQty(l.line_id, q))}
              onRemove={() => (l.line_id.startsWith("added-") ? removeAdded(l.line_id) : removeLine(l.line_id))} />
          ))}
        </div>
      ))}
      {customReqs.map((c, i) => (
        <div key={i} className="ag-cs-row ag-cs-line" style={{ gridTemplateColumns: "1fr 90px 150px 40px" }} data-testid={`agency-estimator-custom-line-${i}`}>
          <div className="ag-cs-desc">{c.description}</div>
          <div className="ag-cs-num">{c.qty}</div>
          <div className="ag-cs-num" style={{ color: "var(--amber)" }}>NJM to quote</div>
          <button type="button" className="ag-cs-cbtn" onClick={() => removeCustom(i)} data-testid={`agency-estimator-remove-custom-${i}`}><X size={14} /></button>
        </div>
      ))}
      <div className="ag-cs-subtotal-row ag-cs-row" style={{ gridTemplateColumns: "1fr 90px 150px 40px" }}>
        <div>Production Subtotal</div><div /><div className="ag-cs-num">{formatMoney(subtotal, currency)}</div><div />
      </div>
      </div>

      {!addOpen ? (
        <button className="ag-btn ag-btn-ghost" style={{ marginTop: 16 }} onClick={() => setAddOpen(true)} data-testid="agency-estimator-add-requirement"><Plus size={13} /> Add requirement</button>
      ) : (
        <AddRequirementPanel onAdd={addCatalog} onClose={() => setAddOpen(false)} catalogFn={agencyApi.estimatorCatalog} currency={currency} />
      )}

      <div style={{ marginTop: 20 }}>
        <p className="ag-eyebrow" style={{ marginBottom: 8 }}>Reference videos <span style={{ textTransform: "none", fontWeight: 400 }}>· optional</span></p>
        {!refOpen ? (
          <button className="ag-btn ag-btn-ghost" onClick={() => setRefOpen(true)} data-testid="agency-estimator-open-reference-picker"><Plus size={13} /> Attach reference from NJM's work ({referenceVideoIds.length}/5)</button>
        ) : (
          <ReferenceVideoPicker selectedIds={referenceVideoIds} onToggle={toggleReference} onClose={() => setRefOpen(false)} />
        )}
      </div>

      <div className="ag-cs-totals" style={{ marginTop: 26, maxWidth: 380, marginLeft: "auto" }}>
        <div className="ag-cs-total-line"><span>Subtotal</span><span>{formatMoney(subtotal, currency)}</span></div>
        {gstApplicable && <div className="ag-cs-total-line"><span>GST (18%)</span><span>{formatMoney(gst, currency)}</span></div>}
        <div className="ag-cs-total-line ag-cs-grand"><span>Estimated Production Cost</span><span data-testid="agency-estimator-grand-total">{formatMoney(grandTotal, currency)}</span></div>
      </div>

      <div className="ag-small" style={{ marginTop: 22, maxWidth: 520 }}>
        <p style={{ fontWeight: 600, color: "var(--ink)", textTransform: "uppercase", fontSize: 11, letterSpacing: ".06em" }}>Production costs only</p>
        <p style={{ marginTop: 4 }}>Editing, scripting, storyboarding and other creative/post-production services are quoted separately.</p>
        <p style={{ marginTop: 8 }}>Final pricing may change after NJM reviews location, availability, travel, permits, equipment and scope.</p>
      </div>
      <button className="ag-btn ag-btn-gold" style={{ marginTop: 18 }} disabled={submitting} onClick={submit} data-testid="agency-estimator-request-quote">Request Formal Quote</button>
      <p className="ag-small" style={{ marginTop: 8 }}>Nothing is booked by this — NJM confirms first.</p>
    </div>
  );
}

function TvcStep({ form, onEditBasics, onSubmit, submitting }) {
  return (
    <div style={{ maxWidth: 640 }}>
      <h1 className="ag-h1" style={{ fontSize: 30 }}>TVC / Advertising Film — let's build this around your brief.</h1>
      <p className="ag-body" style={{ marginTop: 14 }}>TVC costs vary too much by cast, crew, locations, art, production design and equipment to estimate automatically. Any number we generated from a rate card would be one we couldn't stand behind.</p>
      <p className="ag-body" style={{ marginTop: 10 }}>So we skip the line items. Send us what you've filled in and we'll build the quote around your brief.</p>
      <div style={{ marginTop: 24 }}>
        <Tips title="What we'll send you" items={[
          { h: "A conversation first", b: "Usually a ten-minute call about treatment, cast and usage." },
          { h: "A quote built around the brief", b: "Not assembled from a rate card." },
          { h: "No estimate in the meantime", b: "One number we can stand behind, rather than a range you cannot plan against." },
        ]} />
      </div>
      <button className="ag-btn ag-btn-gold" style={{ marginTop: 22 }} disabled={submitting} onClick={() => onSubmit({})} data-testid="agency-estimator-request-tvc-quote">Request TVC Quote</button>
      <p className="ag-small" style={{ marginTop: 8 }}>Carries your project basics through automatically. Nothing is booked, and no estimate is generated.</p>
      <div className="ag-card" style={{ marginTop: 30 }}>
        <p className="ag-eyebrow" style={{ marginBottom: 12 }}>Your basics</p>
        <div className="ag-grid ag-grid-3" style={{ gap: 14 }}>
          <div><div className="ag-label">Type</div><div className="ag-body">{form.production_type}</div></div>
          <div><div className="ag-label">Location</div><div className="ag-body">{form.shoot_location}</div></div>
          <div><div className="ag-label">Shoot Days</div><div className="ag-body">{form.shoot_days} days</div></div>
        </div>
        <button className="ag-link" style={{ marginTop: 14 }} onClick={onEditBasics} data-testid="agency-estimator-tvc-edit-basics">Edit basics</button>
      </div>
    </div>
  );
}

function FallbackStep({ onEditBasics, onSubmit, submitting }) {
  return (
    <div style={{ maxWidth: 640 }}>
      <h1 className="ag-h1" style={{ fontSize: 30 }} data-testid="agency-estimator-fallback-title">We need to confirm your NJM rate setup before generating an estimate.</h1>
      <p className="ag-body" style={{ marginTop: 14 }}>Your account doesn't yet have a confirmed geography, and that determines which NJM rate structure applies to you. Rather than guess and show you a number from the wrong one, we'd rather set it up properly.</p>
      <p className="ag-body" style={{ marginTop: 10 }}>It's a one-time thing. Send us the project and we'll confirm your setup alongside the quote.</p>
      <div style={{ marginTop: 24 }}>
        <Tips title="What happens next" items={[
          { h: "We confirm your rate setup", b: "A one-time step so future estimates generate instantly." },
          { h: "We price this project", b: "From your basics, at the correct rate structure." },
          { h: "The quote arrives here", b: "And the estimator works normally from then on." },
        ]} />
      </div>
      <button className="ag-btn ag-btn-gold" style={{ marginTop: 22 }} disabled={submitting} onClick={() => onSubmit({})} data-testid="agency-estimator-request-quote-fallback">Request a Quote</button>
      <p className="ag-small" style={{ marginTop: 8 }}>Sends your project basics to NJM. Nothing is booked, and no estimate is generated.</p>
      <button className="ag-link" style={{ marginTop: 14, display: "block" }} onClick={onEditBasics} data-testid="agency-estimator-fallback-edit-basics">Edit basics</button>
    </div>
  );
}

export default function AgencyEstimator() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | basics | estimate | tvc | fallback
  const [ctx, setCtx] = useState(null);
  const [prefill, setPrefill] = useState(null);
  // PM Feedback Batch 1, item 2 (2026-09) — lifted out of EstimateStep so "Edit basics" ->
  // "Continue to Estimate" (same production type) reuses this instead of re-fetching a blank
  // prefill and wiping every edit. `forType` guards against reusing stale data for a
  // DIFFERENT production type (that correctly still re-fetches fresh).
  const [estimateData, setEstimateData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Regression v6 fix pass (2026-10), Part 4 item 1 — in-flight guard + visible state for the
  // prefill fetch behind "Continue to Estimate".
  const [building, setBuilding] = useState(false);
  const [form, setForm] = useState({ production_type: "", shoot_location: "", shoot_days: 1, working_title: "", brief: "", camera_setup: "FX3" });

  useEffect(() => {
    agencyApi.estimatorConfig().then((r) => { setCtx(r.data); setPhase("basics"); }).catch((e) => {
      const code = e?.response?.data?.detail?.code;
      setPhase(code === "estimator_disabled" ? "disabled" : code === "not_a_partner_agency" ? "not_partner_agency" : "unavailable");
    });
  }, []);

  const continueToStep2 = async () => {
    if (building) return;
    if (form.production_type === "TVC") return setPhase("tvc");
    if (!ctx.resolved) return setPhase("fallback");
    // E3/E4 (2026-11): re-use the in-memory estimate only when type AND camera setup AND
    // shoot days all still match what it was built for — any of those changing needs a fresh,
    // freshly-priced prefill (a different package, or a different day-scaling multiplier).
    if (estimateData && estimateData.forType === form.production_type
        && estimateData.forCamera === form.camera_setup && estimateData.forDays === Number(form.shoot_days)) {
      setPhase("estimate");
      return;
    }
    setBuilding(true);
    try {
      const r = await agencyApi.estimatorPrefill(form.production_type, form.brief, form.camera_setup, Number(form.shoot_days) || 1);
      setPrefill(r.data);
      setEstimateData({
        lines: (r.data.lines || []).map((l) => ({ ...l })),
        addedLines: [], customReqs: [], referenceVideoIds: [],
        forType: form.production_type, forCamera: form.camera_setup, forDays: Number(form.shoot_days) || 1,
      });
      setPhase("estimate");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBuilding(false); }
  };

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      const r = await agencyApi.estimatorSubmit({ ...form, shoot_days: Number(form.shoot_days) || 1, prefill_token: prefill?.prefill_token, ...payload });
      nav(`/agency/quote-requests/${r.data.id}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSubmitting(false); }
  };

  // Rolled-Up Wave, Part 1A (2026-10) — direct nav to /agency/estimator while the per-agency
  // "Estimate calculator" flag is off (AgencyShell.jsx already hides the nav button, but a
  // bookmarked/typed URL bypasses that) hits this instead of the normal flow.
  if (phase === "disabled") {
    return (
      <div className="ag-card" data-testid="agency-estimator-disabled">
        <p className="ag-h3" style={{ marginBottom: 8 }}>Estimates are not enabled for your account</p>
        <p className="ag-small">Contact your NJM producer to turn this on.</p>
      </div>
    );
  }
  if (phase === "not_partner_agency") {
    return (
      <div className="ag-card" data-testid="agency-estimator-not-partner-agency">
        <p className="ag-h3" style={{ marginBottom: 8 }}>Estimator isn't set up for your agency yet</p>
        <p className="ag-small">Your account isn't currently flagged as a Partner Agency, so the cost estimator can't run. Contact NJM and we'll get this switched on for you.</p>
      </div>
    );
  }
  if (phase === "unavailable") {
    return (
      <div className="ag-card" data-testid="agency-estimator-unavailable">
        <p className="ag-h3" style={{ marginBottom: 8 }}>Couldn't load the estimator</p>
        <p className="ag-small">Something went wrong loading this page. Please try again in a moment.</p>
      </div>
    );
  }
  // Part B (2026-10), item 5 — a real skeleton instead of a single line of text while the
  // estimator config loads, same `.ag-skeleton` pulse AgencyCostSheet/AgencyBtsGallery use.
  if (phase === "loading" || !ctx) {
    return (
      <div className="ag-stack" data-testid="agency-estimator-loading">
        <div className="ag-skeleton" style={{ height: 20, width: 220, borderRadius: 4, marginBottom: 20 }} />
        <div className="ag-skeleton" style={{ height: 36, width: "60%", borderRadius: 6, marginBottom: 14 }} />
        <div className="ag-skeleton" style={{ height: 16, width: "40%", borderRadius: 4, marginBottom: 30 }} />
        {[0, 1, 2].map((i) => <div key={i} className="ag-skeleton" style={{ height: 44, marginBottom: 14, borderRadius: 6 }} />)}
      </div>
    );
  }

  // PM Feedback Batch 1, item 2 (2026-09) fix — this used to be window.history.back() even
  // when the label read "Project basics", which actually left the whole estimator page (real
  // browser-history navigation) instead of going back one STEP within it. Now it always just
  // changes phase when there's a step to go back to, and only leaves the page from "basics".
  const goBack = () => (phase === "basics" ? nav("/agency") : setPhase("basics"));

  return (
    <div data-testid="agency-estimator-page">
      <button className="ag-link" onClick={goBack} data-testid="agency-estimator-back">&larr; {phase === "basics" ? "Agency home" : "Project basics"}</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "14px 0 26px", flexWrap: "wrap", gap: 8 }}>
        <Steps step={phase === "basics" ? 1 : 2} tvc={phase === "tvc"} fallback={phase === "fallback"} />
        {ctx.geography_label && <span className="ag-small" data-testid="agency-estimator-geography-label">{ctx.geography_label}</span>}
      </div>

      {phase === "basics" && <BasicsStep form={form} setForm={setForm} onContinue={continueToStep2} building={building}
        cameraSelectableTypes={ctx.camera_selectable_types} cameraSetups={ctx.camera_setups} />}
      {phase === "estimate" && prefill && estimateData && (
        <EstimateStep form={form} prefill={prefill} data={estimateData} setData={setEstimateData} currency={ctx.currency} gstApplicable={ctx.gst_applicable}
          onEditBasics={() => setPhase("basics")} onSubmit={submit} submitting={submitting} />
      )}
      {phase === "tvc" && <TvcStep form={form} onEditBasics={() => setPhase("basics")} onSubmit={submit} submitting={submitting} />}
      {phase === "fallback" && <FallbackStep onEditBasics={() => setPhase("basics")} onSubmit={submit} submitting={submitting} />}
    </div>
  );
}
